import { all, call, cancelled, delay, put, race, select, takeLatest } from 'redux-saga/effects';
import { faceMatchActions, FACE_MATCH_NO_FACE_MESSAGE, getFaceMatchFailureMessage, SUCCESS_THRESHOLD } from './faceMatchSlice';
import { compareFaces, extractSimilarity, isFaceMatchSuccessful } from 'services/faceMatchingService';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { selectFaceMatchConfigByTransactionId } from 'store/formControlSlice/formControlSelectors';
import { FACE_MATCH_FAILURE, meetsFaceMatchThreshold } from 'utils/faceMatchEnforcement';

// The submit dialog stays locked while a face match runs, so a request that never answers must still end.
export const FACE_MATCH_TIMEOUT_MS = 30000;

const getMatchType = (sourceCameraType, targetCameraType) => {
    const types = [sourceCameraType, targetCameraType].filter(Boolean);
    if (types.some((t) => t?.includes('TRUCK'))) return 'TRUCK';
    if (types.some((t) => t?.includes('CAR'))) return 'CAR';
    return null;
};

export function* handleCompareFaces({ payload }) {
    const { sourceImage, targetImage, sourceCameraType, targetCameraType, transactionId, matchType: explicitMatchType } = payload;

    const matchType = explicitMatchType || getMatchType(sourceCameraType, targetCameraType);

    try {
        const { response, timedOut } = yield race({
            response: call(compareFaces, sourceImage, targetImage),
            timedOut: delay(FACE_MATCH_TIMEOUT_MS),
        });
        if (timedOut) {
            const timeoutError = new Error('The face match service did not respond in time.');
            timeoutError.code = FACE_MATCH_FAILURE.TIMEOUT;
            throw timeoutError;
        }

        const similarity = extractSimilarity(response);
        // On an enforcing gate the gate's threshold decides, so the toast agrees with the submit block.
        const { enforced, threshold } = yield select(selectFaceMatchConfigByTransactionId(transactionId));
        const requiredScore = enforced ? threshold : SUCCESS_THRESHOLD;
        const isMatch = enforced ? meetsFaceMatchThreshold(similarity, threshold) : isFaceMatchSuccessful(response);

        yield put(
            faceMatchActions.compareFacesSuccess({
                response,
                similarity,
                isMatch,
                transactionId,
                matchType,
                sourceImage,
                targetImage,
            })
        );

        if (isMatch) {
            console.log('[FaceMatch] SUCCESS', {
                transactionId,
                matchType,
                similarity: similarity?.toFixed(2),
                confidence: response?.FaceMatches?.[0]?.Face?.Confidence?.toFixed(2) || null,
            });

            yield put(
                snackbarActions.enqueueSnackbar({
                    message: `Face match successful! Similarity: ${similarity?.toFixed(2)}%`,
                    options: { variant: 'success' },
                })
            );
        } else if (similarity !== null) {
            console.log('[FaceMatch] LOW_CONFIDENCE', {
                transactionId,
                matchType,
                similarity: similarity?.toFixed(2),
                confidence: response?.FaceMatches?.[0]?.Face?.Confidence?.toFixed(2) || null,
            });

            yield put(
                snackbarActions.enqueueSnackbar({
                    message: `Face match below ${requiredScore}% threshold. Similarity: ${similarity?.toFixed(2)}%`,
                    options: { variant: 'warning' },
                })
            );
        } else {
            console.log('[FaceMatch] MISMATCH', {
                transactionId,
                matchType,
                similarity: null,
                confidence: response?.SourceImageFace?.Confidence?.toFixed(2) || null,
            });

            yield put(
                snackbarActions.enqueueSnackbar({
                    message: FACE_MATCH_NO_FACE_MESSAGE,
                    options: { variant: 'warning' },
                })
            );
        }
    } catch (error) {
        console.error('[FaceMatch] ERROR', {
            transactionId,
            matchType,
            errorMessage: error.message || 'Face comparison failed',
        });

        yield put(
            faceMatchActions.compareFacesFailure({
                transactionId,
                error: {
                    message: error.message || 'Face comparison failed',
                    code: error.code || 'UNKNOWN_ERROR',
                },
            })
        );

        yield put(
            snackbarActions.enqueueSnackbar({
                message: getFaceMatchFailureMessage(error),
                options: { variant: 'warning' },
            })
        );
    } finally {
        if (yield cancelled()) yield put(faceMatchActions.compareFacesCancelled({ transactionId }));
    }
}

export function* faceMatchSaga() {
    yield all([takeLatest(faceMatchActions.compareFaces.type, handleCompareFaces)]);
}
